import { useScienceTutorSection } from './scienceTutorContext';
import { useState } from 'react';

import { ChoicePanel, FeedbackBox } from './PolymersShared';
import {
  ALKALIS,
  BALANCE_ELEMENTS,
  checkMicellePrediction,
  checkScumPrediction,
  HEAD_GROUP_FORMULA,
  micelleLayout,
  saponificationQuestion,
  saponify,
  SOAP_FATS,
  SOAP_QUESTIONS,
  SURFACTANTS,
  WATER_TYPES,
  type Alkali,
  type MicellePart,
  type Saponification,
  type Surfactant,
  type WaterType,
} from './polymersSoaps';
import { CARD_CLASS, chipClass } from './polymersStyles';

const STRUCTURE_WIDTH = 340;
const STRUCTURE_HEIGHT = 72;
const ZIGZAG_STEP = 14;
const ZIGZAG_RISE = 9;
const HEAD_RADIUS = 15;
const MICELLE_SIZE = 240;
const MICELLE_HEAD_RADIUS = 6;

interface Feedback {
  correct: boolean;
  text: string;
}

function AtomBalance({ value }: { value: Saponification }) {
  const elements = BALANCE_ELEMENTS.filter(
    (element) => (value.reactantCounts[element] ?? 0) > 0 || (value.productCounts[element] ?? 0) > 0,
  );
  return (
    <table className="mt-3 w-full text-left text-sm" aria-label="Atom balance">
      <thead>
        <tr className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
          <th className="py-1">Element</th>
          <th className="py-1">Reactants</th>
          <th className="py-1">Products</th>
        </tr>
      </thead>
      <tbody>
        {elements.map((element) => {
          const left = value.reactantCounts[element] ?? 0;
          const right = value.productCounts[element] ?? 0;
          return (
            <tr key={element} className="border-t border-hairline">
              <td className="py-1.5 font-bold text-ink">{element}</td>
              <td className="py-1.5 font-mono text-ink">{left}</td>
              <td className="py-1.5 font-mono text-ink">
                {right} {left === right ? '✓' : '✗'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SaponificationPanel() {
  const [fatId, setFatId] = useState(SOAP_FATS[1]?.id ?? SOAP_FATS[0].id);
  const [alkali, setAlkali] = useState<Alkali>('NaOH');
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('SaponificationPanel', { fatId, alkali, revealed });
  const result = saponify(fatId, alkali);
  if (!result.ok) {
    return <FeedbackBox correct={false}>{result.message}</FeedbackBox>;
  }
  const value = result.value;
  const seed = SOAP_FATS.findIndex((fat) => fat.id === fatId) + ALKALIS.indexOf(alkali);
  const question = saponificationQuestion(value, seed);

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <section className={CARD_CLASS} aria-labelledby="saponify-heading">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">1 · Predict</p>
        <h3 id="saponify-heading" className="mt-1 text-xl font-extrabold text-ink">Saponification</h3>
        <div className="mt-3 grid grid-cols-2 gap-2" role="group" aria-label="Choose a fat or oil">
          {SOAP_FATS.map((fat) => (
            <button
              key={fat.id}
              type="button"
              aria-pressed={fat.id === fatId}
              onClick={() => {
                setFatId(fat.id);
                setRevealed(false);
              }}
              className={chipClass(fat.id === fatId)}
            >
              {fat.triglycerideName}
            </button>
          ))}
        </div>
        <div className="mt-2 flex gap-2" role="group" aria-label="Choose an alkali">
          {ALKALIS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === alkali}
              onClick={() => {
                setAlkali(option);
                setRevealed(false);
              }}
              className={chipClass(option === alkali)}
            >
              {option}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <ChoicePanel key={question.id} question={question} onChecked={() => setRevealed(true)} />
        </div>
      </section>

      <section className={CARD_CLASS} aria-labelledby="saponify-model-heading">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">2 · Model</p>
        <h3 id="saponify-model-heading" className="mt-1 text-xl font-extrabold text-ink">
          Three ester links → three soap ions
        </h3>
        {revealed ? (
          <div>
            <p className="mt-3 overflow-x-auto rounded-xl bg-surface px-3 py-2 font-mono text-sm font-bold text-ink">
              {value.equation}
            </p>
            <ul className="mt-3 grid gap-1 font-mono text-xs font-bold text-ink-soft" aria-label="Bonds broken">
              {['CH₂', 'CH', 'CH₂'].map((carbon, index) => (
                <li key={index} className="overflow-x-auto rounded-lg bg-wash-sky px-3 py-1.5">
                  {carbon}–O–CO–{value.fattyAcid.chain} + OH⁻ → {carbon}–OH + ⁻OOC–{value.fattyAcid.chain}
                </li>
              ))}
            </ul>
            <AtomBalance value={value} />
            <p className="mt-2 text-xs font-bold text-slate2">
              {value.balanced ? 'Balanced: every atom on the left appears on the right.' : 'Not balanced.'}{' '}
              Soap: {value.soapName}, {value.soapFormula}.
            </p>
          </div>
        ) : (
          <p className="mt-3 rounded-2xl bg-wash-sky p-4 text-sm font-bold leading-6 text-slate2">
            Check an answer first. The balanced equation appears after your prediction.
          </p>
        )}
      </section>
    </div>
  );
}

function SurfactantStructure({ surfactant }: { surfactant: Surfactant }) {
  const startX = 12;
  const midY = STRUCTURE_HEIGHT / 2;
  const points = Array.from({ length: surfactant.tailCarbons }, (_, index) => ({
    x: startX + index * ZIGZAG_STEP,
    y: midY + (index % 2 === 0 ? ZIGZAG_RISE : -ZIGZAG_RISE),
  }));
  const last = points[points.length - 1];
  const headX = last.x + ZIGZAG_STEP + HEAD_RADIUS;
  const width = Math.max(STRUCTURE_WIDTH, headX + HEAD_RADIUS + 8);
  return (
    <svg
      viewBox={`0 0 ${width} ${STRUCTURE_HEIGHT + 22}`}
      className="block h-auto w-full"
      role="img"
      aria-label={`${surfactant.name}: hydrophobic tail of ${surfactant.tailCarbons} carbons and a ${surfactant.headGroup} head ${HEAD_GROUP_FORMULA[surfactant.headGroup]}`}
    >
      <polyline
        points={[...points, { x: headX - HEAD_RADIUS, y: midY }].map((point) => `${point.x},${point.y}`).join(' ')}
        className="fill-none stroke-ink"
        strokeWidth={2}
      />
      {points.map((point, index) => (
        <circle key={index} cx={point.x} cy={point.y} r={2.5} className="fill-slate2" />
      ))}
      <circle cx={headX} cy={midY} r={HEAD_RADIUS} className="fill-wash-coral stroke-brand-coral" strokeWidth={2} />
      <text x={headX} y={midY + 4} textAnchor="middle" className="fill-ink text-[11px] font-extrabold">
        −
      </text>
      <text x={startX} y={STRUCTURE_HEIGHT + 16} className="fill-slate2 text-[10px] font-bold">
        non-polar tail (hydrophobic)
      </text>
      <text x={headX + HEAD_RADIUS} y={STRUCTURE_HEIGHT + 16} textAnchor="end" className="fill-ink text-[10px] font-bold">
        ionic head (hydrophilic)
      </text>
    </svg>
  );
}

function MicelleDiagram({ surfactant }: { surfactant: Surfactant }) {
  const centre = MICELLE_SIZE / 2;
  const layout = micelleLayout(surfactant, centre, centre);
  return (
    <svg
      viewBox={`0 0 ${MICELLE_SIZE} ${MICELLE_SIZE}`}
      className="mx-auto block h-auto w-full max-w-xs"
      role="img"
      aria-label={`Micelle of ${layout.molecules.length} ${surfactant.name} ions: tails point into the oil droplet, heads face the water`}
    >
      <rect x={0} y={0} width={MICELLE_SIZE} height={MICELLE_SIZE} rx={24} className="fill-wash-sky" />
      <circle cx={centre} cy={centre} r={layout.coreRadius} className="fill-wash-sunshine stroke-brand-sunshine" strokeWidth={1.5} strokeDasharray="4 3" />
      <text x={centre} y={centre + 4} textAnchor="middle" className="fill-ink text-[11px] font-extrabold">oil</text>
      {layout.molecules.map((molecule, index) => (
        <g key={index}>
          <line x1={molecule.tailX} y1={molecule.tailY} x2={molecule.headX} y2={molecule.headY} className="stroke-ink" strokeWidth={1.5} />
          <circle cx={molecule.headX} cy={molecule.headY} r={MICELLE_HEAD_RADIUS} className="fill-wash-coral stroke-brand-coral" strokeWidth={1.5} />
        </g>
      ))}
      <text x={12} y={20} className="fill-ink text-[10px] font-bold">water</text>
    </svg>
  );
}

function StructurePanel({ surfactant, onChoose }: { surfactant: Surfactant; onChoose: (id: string) => void }) {
  const [micelleFeedback, setMicelleFeedback] = useState<Feedback | null>(null);
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <section className={CARD_CLASS} aria-labelledby="structure-heading">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">3 · Compare</p>
        <h3 id="structure-heading" className="mt-1 text-xl font-extrabold text-ink">Soap or synthetic detergent?</h3>
        <div className="mt-3 grid gap-2 sm:grid-cols-2" role="group" aria-label="Choose a surfactant">
          {SURFACTANTS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={item.id === surfactant.id}
              onClick={() => onChoose(item.id)}
              className={chipClass(item.id === surfactant.id)}
            >
              {item.name}
            </button>
          ))}
        </div>
        <p className="mt-4 overflow-x-auto font-mono text-sm font-bold text-ink">{surfactant.condensed}</p>
        <p className="mt-1 text-sm font-medium leading-6 text-slate2">
          {surfactant.kind === 'soap' ? 'Soap' : 'Synthetic detergent'} · {surfactant.headGroup} head{' '}
          {HEAD_GROUP_FORMULA[surfactant.headGroup]} · {surfactant.tailNote}
        </p>
        <div className="mt-3 overflow-x-auto">
          <SurfactantStructure surfactant={surfactant} />
        </div>
      </section>

      <section className={CARD_CLASS} aria-labelledby="micelle-heading">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">4 · Micelle</p>
        <h3 id="micelle-heading" className="mt-1 text-xl font-extrabold text-ink">Which end goes into the oil?</h3>
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Part that points into the oil">
          {(['head', 'tail'] as MicellePart[]).map((part) => (
            <button
              key={part}
              type="button"
              onClick={() => {
                const result = checkMicellePrediction(part);
                setMicelleFeedback({ correct: result.correct, text: result.feedback });
              }}
              className={chipClass(false)}
            >
              {part === 'head' ? 'The ionic head' : 'The hydrocarbon tail'}
            </button>
          ))}
        </div>
        {micelleFeedback ? (
          <div className="mt-4 grid gap-4">
            <FeedbackBox correct={micelleFeedback.correct}>{micelleFeedback.text}</FeedbackBox>
            <MicelleDiagram surfactant={surfactant} />
          </div>
        ) : null}
      </section>
    </div>
  );
}

function HardWaterPanel({ surfactant }: { surfactant: Surfactant }) {
  const [water, setWater] = useState<WaterType>('hard-calcium');
  useScienceTutorSection('HardWaterPanel', { water });
  const [result, setResult] = useState<ReturnType<typeof checkScumPrediction>>(null);
  return (
    <section className={CARD_CLASS} aria-labelledby="hard-water-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">5 · Hard water</p>
      <h3 id="hard-water-heading" className="mt-1 text-xl font-extrabold text-ink">
        Will {surfactant.name} form scum?
      </h3>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Choose the water">
        {WATER_TYPES.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={option.id === water}
            onClick={() => {
              setWater(option.id);
              setResult(null);
            }}
            className={chipClass(option.id === water)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Your prediction">
        {[true, false].map((predictsScum) => (
          <button
            key={String(predictsScum)}
            type="button"
            onClick={() => setResult(checkScumPrediction(surfactant.id, water, predictsScum))}
            className="btn-pill-primary"
          >
            {predictsScum ? 'Scum forms' : 'No scum'}
          </button>
        ))}
      </div>
      {result ? (
        <div className="mt-4">
          <FeedbackBox correct={result.correct}>
            {result.feedback}
            {result.outcome.equation ? (
              <span className="mt-2 block overflow-x-auto font-mono font-bold text-ink">
                {result.outcome.equation}
              </span>
            ) : null}
          </FeedbackBox>
        </div>
      ) : null}
    </section>
  );
}

function QuestionBank() {
  const [index, setIndex] = useState(0);
  useScienceTutorSection('QuestionBank', { index });
  const question = SOAP_QUESTIONS[index];
  return (
    <section className={CARD_CLASS} aria-labelledby="soap-questions-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="soap-questions-heading" className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          6 · Check your understanding
        </h3>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Soap question">
          {SOAP_QUESTIONS.map((item, itemIndex) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={itemIndex === index}
              aria-label={`Soap question ${itemIndex + 1}`}
              onClick={() => setIndex(itemIndex)}
              className={`h-9 min-w-9 rounded-full border px-3 text-sm font-extrabold ${
                itemIndex === index ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'
              }`}
            >
              {itemIndex + 1}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <ChoicePanel key={question.id} question={question} />
      </div>
    </section>
  );
}

export function PolymersSoapsMode() {
  const [surfactantId, setSurfactantId] = useState(SURFACTANTS[0].id);
  useScienceTutorSection('PolymersSoapsMode', { surfactantId });
  const surfactant = SURFACTANTS.find((item) => item.id === surfactantId) ?? SURFACTANTS[0];
  return (
    <div className="grid gap-6">
      <SaponificationPanel />
      <StructurePanel surfactant={surfactant} onChoose={setSurfactantId} />
      <div className="grid gap-6 xl:grid-cols-2">
        <HardWaterPanel key={surfactant.id} surfactant={surfactant} />
        <QuestionBank />
      </div>
    </div>
  );
}
